import * as Popover from '@radix-ui/react-popover'
import { Check, MoreHorizontal } from 'lucide-react'
import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Tooltip } from '@/components/ui/Tooltip'
import { useApproveWeek, useReopenWeek } from '@/lib/time-queries'
import { formatDayLabel } from '@/lib/week'
import { monthEndInWeek } from './week-rows'
import type { WeekApproval } from '@/lib/types'

interface WeekApprovalControlProps {
  approval: WeekApproval | null | undefined
  weekId: string
  personId?: string
  editable: boolean
  /** Benoemt de week op de knop; nodig zodra de knop buiten het weekoverzicht staat. */
  weekLabel?: string
  /** Dagen zonder uren waar wel uren verwacht worden; niet-leeg vraagt om bevestiging. */
  emptyWorkdays?: string[]
}

interface ConfirmApprovalProps {
  weekLabel: string
  emptyWorkdays: string[]
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}

function ConfirmApproval({ weekLabel, emptyWorkdays, pending, onCancel, onConfirm }: ConfirmApprovalProps) {
  const days = emptyWorkdays.join(', ')
  const plural = emptyWorkdays.length === 1 ? 'dag' : 'dagen'

  return (
    <Modal
      open
      onOpenChange={(value) => !value && onCancel()}
      title={`${weekLabel} afsluiten`}
      description="Afsluiten geldt voor de hele week, niet alleen voor de dag die je nu bekijkt."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Annuleren
          </button>
          <button type="button" className="btn-primary" onClick={onConfirm} disabled={pending}>
            Toch afsluiten
          </button>
        </>
      }
    >
      <p className="text-sm text-ink-soft">
        {`Op ${emptyWorkdays.length} ${plural} staan nog geen uren: ${days}.`}
      </p>
    </Modal>
  )
}

function approvedLabel(approvedAt: string | null): string {
  if (!approvedAt) return 'Afgesloten'
  return `Afgesloten ${formatDayLabel(approvedAt.slice(0, 10))}`
}

/**
 * De knop die de hele week afsluit, met de bevestiging over lege werkdagen eraan.
 * Staat zowel op een open als op een deels afgesloten week, vandaar apart.
 */
function CloseWeekButton({
  label,
  weekLabel,
  emptyWorkdays,
  pending,
  onClose,
}: {
  label: string
  weekLabel?: string
  emptyWorkdays?: string[]
  pending: boolean
  onClose: () => void
}) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const needsConfirm = (emptyWorkdays?.length ?? 0) > 0

  return (
    <>
      <button
        type="button"
        className="btn-primary"
        disabled={pending}
        onClick={() => (needsConfirm ? setConfirmOpen(true) : onClose())}
      >
        <Check className="h-4 w-4" />
        {label}
      </button>
      {confirmOpen ? (
        <ConfirmApproval
          weekLabel={weekLabel ?? 'Deze week'}
          emptyWorkdays={emptyWorkdays ?? []}
          pending={pending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false)
            onClose()
          }}
        />
      ) : null}
    </>
  )
}

/** Het heropen-menu; hangt zowel aan een volledig als aan een deels afgesloten week. */
function ReopenMenu({ onReopen, disabled }: { onReopen: () => void; disabled: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="btn-ghost h-7 w-7 p-0" aria-label="Weekacties">
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="z-40 w-40 rounded-card border border-hairline bg-surface p-1.5 shadow-pop animate-fade-in"
        >
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setOpen(false)
              onReopen()
            }}
            className="block w-full rounded-md px-3 py-2 text-left text-sm text-ink hover:bg-fill-2"
          >
            Heropenen
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

export function WeekApprovalControl({
  approval,
  weekId,
  personId,
  editable,
  weekLabel,
  emptyWorkdays,
}: WeekApprovalControlProps) {
  const approve = useApproveWeek(weekId, personId)
  const reopen = useReopenWeek(weekId, personId)
  // Loopt de week over een maandgrens, dan kun je de maand hier afsluiten zonder de
  // rest van de week dicht te gooien; anders is er niets te knippen.
  const partialThrough = monthEndInWeek(weekId)

  if (!approval) return null

  const partialLabel = approval.approvedThrough ? `Afgesloten t/m ${formatDayLabel(approval.approvedThrough)}` : 'Deels afgesloten'

  if (!editable) {
    if (approval.state === 'open') return null
    const body = (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-surface px-2.5 py-1 text-sm text-ink-soft">
        <Check className="h-3.5 w-3.5 text-accent-600" aria-hidden />
        {approval.state === 'changed'
          ? 'Gewijzigd na akkoord'
          : approval.state === 'partial'
            ? partialLabel
            : approvedLabel(approval.approvedAt)}
      </span>
    )
    return approval.approvedByName ? (
      <Tooltip content={`Afgesloten door ${approval.approvedByName}`}>{body}</Tooltip>
    ) : (
      body
    )
  }

  const label = weekLabel ? `${weekLabel} afsluiten` : 'Week afsluiten'

  if (approval.state === 'open') {
    return (
      <>
        {partialThrough ? (
          <Tooltip content={`Sluit alleen de dagen t/m ${formatDayLabel(partialThrough)} af, zodat die maand gefactureerd kan worden. De rest van de week blijft open.`}>
            <button
              type="button"
              className="btn-secondary"
              disabled={approve.isPending}
              onClick={() => approve.mutate(partialThrough)}
            >
              Sluit af t/m {formatDayLabel(partialThrough)}
            </button>
          </Tooltip>
        ) : null}
        <CloseWeekButton
          label={label}
          weekLabel={weekLabel}
          emptyWorkdays={emptyWorkdays}
          pending={approve.isPending}
          onClose={() => approve.mutate(undefined)}
        />
      </>
    )
  }

  if (approval.state === 'partial') {
    return (
      <div className="inline-flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-md border border-hairline bg-surface pl-2.5 pr-1 text-sm text-ink-soft">
          <Tooltip content={approval.approvedByName ? `Afgesloten door ${approval.approvedByName}` : null}>
            <span className="inline-flex items-center gap-1.5 py-1">
              <Check className="h-3.5 w-3.5 text-accent-600" aria-hidden />
              {partialLabel}
            </span>
          </Tooltip>
          <ReopenMenu onReopen={() => reopen.mutate()} disabled={reopen.isPending} />
        </span>
        <CloseWeekButton
          label="Rest van de week afsluiten"
          weekLabel={weekLabel}
          emptyWorkdays={emptyWorkdays}
          pending={approve.isPending}
          onClose={() => approve.mutate(undefined)}
        />
      </div>
    )
  }

  if (approval.state === 'changed') {
    return (
      <div className="inline-flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-fill-1 px-2.5 py-1 text-sm text-ink-soft">
          <span className="inline-block h-2 w-2 rounded-full bg-status-approval" aria-hidden />
          Gewijzigd na akkoord
        </span>
        <button
          type="button"
          className="btn-ghost h-8 px-2.5 text-sm"
          disabled={approve.isPending}
          onClick={() => approve.mutate(undefined)}
        >
          Opnieuw bevestigen
        </button>
      </div>
    )
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-hairline bg-surface pl-2.5 pr-1 text-sm text-ink-soft">
      <Tooltip content={approval.approvedByName ? `Afgesloten door ${approval.approvedByName}` : null}>
        <span className="inline-flex items-center gap-1.5 py-1">
          <Check className="h-3.5 w-3.5 text-accent-600" aria-hidden />
          {approvedLabel(approval.approvedAt)}
        </span>
      </Tooltip>
      <ReopenMenu onReopen={() => reopen.mutate()} disabled={reopen.isPending} />
    </span>
  )
}
